import { DATASETS_UNSYNCABLE } from 'js/Constants';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { find, has, isEmpty, isNil, reduce, replace, split, startsWith } from 'lodash';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { DATASET_OUTPUT_BASE_URL, WEBHOOK_SOURCE_URL } from '../constants/api';
import { CORS_URL, EXT_CATALOG_ENABLED } from '../constants/config';
import JSONUtils from './JSONUtils';

const LEGACY_CORS_URL_PROD = 'https://dataops.nexla.io/cors/';
const LEGACY_CORS_URL_STAGE = 'https://stage.nexla.com/cors/';

export const getCorsCompatibleURL = (fromUrl = '') => {
  if (isNil(fromUrl)) {
    return fromUrl;
  }

  let corsUrl = startsWith(fromUrl, LEGACY_CORS_URL_PROD)
    ? replace(fromUrl, LEGACY_CORS_URL_PROD, CORS_URL)
    : startsWith(fromUrl, LEGACY_CORS_URL_STAGE)
      ? replace(fromUrl, LEGACY_CORS_URL_STAGE, CORS_URL)
      : fromUrl;

  corsUrl = startsWith(corsUrl, CORS_URL) ? corsUrl : CORS_URL + corsUrl;
  return corsUrl;
};

export const getDatasetDataUrl = (datasetId = null, apiKey = '') => {
  if (isNil(datasetId)) {
    return '';
  }

  return `${DATASET_OUTPUT_BASE_URL()}/sync/${datasetId}?api_key=${apiKey}`;
};

export const isDatasetSyncable = (dataset = null) => {
  const isUnsyncable = DATASETS_UNSYNCABLE.includes(dataset?.flow_type);

  if (isUnsyncable) {
    return false;
  }

  return !isNil(dataset) && has(dataset, 'sync_api_config') && !isNil(dataset.sync_api_config);
};

export const isSharedDataset = (dataset = null) => {
  return (
    (dataset?.node_type === 'data_set' && dataset?.isShared) ||
    dataset?.access_roles?.includes('sharer')
  );
};

export const getDatasetSyncPayloadString = (syncConfig = null) => {
  if (isEmpty(syncConfig) || !has(syncConfig, 'params') || isEmpty(syncConfig.params)) {
    return '{}';
  }

  const payload = reduce(
    syncConfig.params,
    (acc, thisP) => {
      acc[thisP.param] = has(thisP, 'default') ? thisP.default : null;
      return acc;
    },
    {},
  );

  return JSONUtils.stringifyJson(payload, 2);
};

export const getDatasetFeedUrlSampleRequest = (
  url,
  sanitizedUrl,
  data = `{
  }`,
  contentType = 'application/json',
) => {
  let payload = `-d '${data}'`;

  return {
    sample: `curl -X POST ${url} \
 -H 'Content-Type: ${contentType}' \
 ${payload}`,
    sanitizedSample: `curl -X POST ${sanitizedUrl} \
 -H 'Content-Type: ${contentType}' \
 ${payload}`,
  };
};

export const getWebhookUrl = (
  webhookId = null,
  currentOrg = null,
  apiKeys = null,
  useApiKey = null,
  path = '/data',
) => {
  if (isNil(webhookId)) {
    return '';
  }

  let orgWebhookURL =
    currentOrg && has(currentOrg, 'org_webhook_host') ? currentOrg.org_webhook_host : null;

  const apiKey = !isNil(useApiKey) ? useApiKey : getUsableApiKey(apiKeys);

  return `${WEBHOOK_SOURCE_URL(orgWebhookURL, path)}/${webhookId}?api_key=${apiKey}`;
};

export const getUsableApiKey = (apiKeys = null) => {
  return getUsableApiKeyEntity(apiKeys)?.api_key || '';
};

export const getUsableApiKeyEntity = (apiKeys = null) => {
  return !isNil(apiKeys)
    ? reduce(
        apiKeys,
        (res, k) => {
          // pick the first api key. override it if there is a more recent active api key
          res = res === null ? k : k.status === 'ACTIVE' ? k : res;
          return res;
        },
        null,
      )
    : null;
};

export const sanitizeApiKeyURL = (url = '', sanitizeAfter = 'api_key=') => {
  const splitParts = split(url, sanitizeAfter);
  return splitParts[0] + sanitizeAfter + sanitizeApiKey(splitParts[1]);
};

export const sanitizeApiKey = (apiKey) => {
  const displayedCharsCount = 5;

  return apiKey.length > displayedCharsCount
    ? apiKey.substr(0, displayedCharsCount) + '*'.repeat(apiKey.length - displayedCharsCount)
    : '*'.repeat(displayedCharsCount);
};

export const getExtCatalogSettings = (data = null) => {
  let catalogEnabled = false;
  let activeCatalog = null;
  if (
    EXT_CATALOG_ENABLED === true &&
    data &&
    has(data, 'catalog_configs') &&
    !isEmpty(data.catalog_configs)
  ) {
    activeCatalog = find(data.catalog_configs, (c) => {
      return c.status === 'ACTIVE';
    });
    catalogEnabled = !isNil(activeCatalog) ? true : false;
  }
  return {
    enabled: catalogEnabled,
    catalog: !isNil(activeCatalog) ? activeCatalog : null,
  };
};

export const getExtCatalogReference = (data = null, catalogConfigId = null) => {
  let refStatus = 'NONE';
  let info = null;
  if (data && has(data, 'catalog_refs') && !isEmpty(data.catalog_refs)) {
    info = find(data.catalog_refs, (r) => {
      return r.catalog_config_id === catalogConfigId;
    });
    if (!isNil(info)) {
      refStatus = info.status;
    }
  }

  return {
    status: refStatus,
    info: !isNil(info) ? info : null,
  };
};

export const isNUIURLStartsWith = (url, startsWithExpectation) => {
  return url.startsWith(startsWithExpectation);
};

export const isNUIURLEquals = (context, url, expectation) => {
  if (!url) {
    return false;
  }

  const clearUrl = url.startsWith(context) ? url.replace(context, '') : url;

  return stripQueryParams(clearUrl) === stripQueryParams(expectation);
};

export const formatNUIURL = (context, url) => {
  return `${context}${url}`;
};

export const isFullScreenCanvasURL = (url) => {
  return new RegExp(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/(\\w+/)?\\d+/edit`).test(url);
};

const stripQueryParams = (url) => {
  return url?.replace(/\?.*/g, '') || '';
};
